import { useCallback, useMemo, useState } from 'react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';

/** Result of sync operation */
export interface SyncResult {
  success: boolean;
  error?: Error;
}

/**
 * Hook for syncing organization preferences (divisions, platforms)
 * to the user-settings storage bucket.
 *
 * This is used by both ProfileSetupWizard and UserProfileSettings
 * to keep the org preferences in sync with the user profile.
 */
export const useSyncOrgPreferences = () => {
  const storageBase = useApi(storageApiRef);
  // forBucket() returns a new object every call; memoize to keep a stable
  // reference so useCallback([storageApi]) below doesn't recreate on every render.
  const storageApi = useMemo(
    () => storageBase.forBucket('user-settings'),
    [storageBase],
  );
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Sync divisions and platforms to the user-settings storage bucket.
   * This allows other components that depend on org preferences to access them.
   * Uses Promise.all for parallel writes (best-effort consistency, not atomic).
   * @returns SyncResult indicating success or failure
   */
  const syncOrgPreferences = useCallback(
    async (divisions: string[], platforms: string[]): Promise<SyncResult> => {
      setSyncing(true);
      setError(null);

      try {
        // Parallel writes for better performance (note: not atomic - partial writes possible on failure)
        await Promise.all([
          storageApi.set('division', divisions),
          storageApi.set('platform', platforms),
          storageApi.set('userOrgInfo', true),
        ]);
        return { success: true };
      } catch (err) {
        const syncError =
          err instanceof Error
            ? err
            : new Error('Failed to sync organization preferences');
        setError(syncError);
        return { success: false, error: syncError };
      } finally {
        setSyncing(false);
      }
    },
    [storageApi],
  );

  /**
   * Clear the sync error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear all organization preferences from storage.
   * Used when resetting the user profile.
   * Uses Promise.all for parallel removal (best-effort, not atomic).
   * @returns SyncResult indicating success or failure
   */
  const clearOrgPreferences = useCallback(async (): Promise<SyncResult> => {
    setSyncing(true);
    setError(null);

    try {
      // Parallel removal for better performance (note: not atomic - partial removal possible on failure)
      await Promise.all([
        storageApi.remove('division'),
        storageApi.remove('platform'),
        storageApi.remove('userOrgInfo'),
      ]);
      return { success: true };
    } catch (err) {
      const removeError =
        err instanceof Error
          ? err
          : new Error('Failed to clear organization preferences');
      setError(removeError);
      return { success: false, error: removeError };
    } finally {
      setSyncing(false);
    }
  }, [storageApi]);

  return {
    syncOrgPreferences,
    clearOrgPreferences,
    syncing,
    error,
    clearError,
  };
};
